import type { NextComponentType } from 'next';
import type { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import React from 'react';

import type { ThemeNames } from '@sravni/design-system-theme';
import { ThemeName } from '@sravni/design-system-theme';
import type { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { ConfigProvider, DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import { formatUserData } from '@sravni/utils/lib/auth';

import { StoreProvider } from '../providers/store';
import type { GlobalState } from '../store';
import { getOrCreateStore } from '../store';
import { setCurrentLocation } from '../store/reducers/locations';
import { setUser } from '../store/reducers/user';
import '@sravni/design-system-theme/lib/globalStyles.css';
import 'intersection-observer';
import '../styles/global.scss';

const runtimeConfig = {
  gatewayUrl: getConfig().publicRuntimeConfig.gatewayUrl,
  publicUrl: getConfig().publicRuntimeConfig.publicUrl,
  environment: getConfig().publicRuntimeConfig.environment,
};

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
}

const MyApp: NextComponentType<App.ReduxAppContext, AppInitialProps, IProps> = (props) => {
  const { Component, pageProps, initialReduxState, deviceInfo, theme } = props;
  const reduxStore = getOrCreateStore(initialReduxState);
  const { publicRuntimeConfig } = getConfig();
  const isProduction = publicRuntimeConfig.environment === 'production';

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { queue } = require('../utils/queue');

    if (isProduction) {
      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message);
          });
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StoreProvider store={reduxStore}>
      <DeviceInfoProvider initialInfo={deviceInfo}>
        <ThemeProvider initialTheme={theme}>
          <ConfigProvider value={runtimeConfig}>
            <Component {...pageProps} />
          </ConfigProvider>
        </ThemeProvider>
      </DeviceInfoProvider>
    </StoreProvider>
  );
};

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  let pageProps = {};
  const reduxStore = getOrCreateStore();
  ctx.store = reduxStore;

  if (typeof window === 'undefined') {
    if (ctx.req.__USER__) {
      reduxStore.dispatch(setUser(formatUserData(ctx.req.__USER__)));
    }
    if (ctx.req.__SELECTED_LOCATION__) {
      reduxStore.dispatch(setCurrentLocation(ctx.req.__SELECTED_LOCATION__));
    }
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  let deviceInfo: DeviceInfo = { phone: false, tablet: false, isAppleMobile: false };
  let theme: ThemeNames = ThemeName.lager;

  if (ctx.req && ctx.req.__DEVICE_INFO__) {
    deviceInfo = ctx.req.__DEVICE_INFO__;
  }
  if (ctx.req && ctx.req.__THEME__) {
    theme = ctx.req.__THEME__;
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    deviceInfo,
    realPath: ctx.req?.url || '',
    theme,
  };
};

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }

export default MyApp;
